import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { applicationService } from '@/services/applications';
import useVuelidate from '@vuelidate/core';
import { email, minLength, numeric, requiredIf } from '@vuelidate/validators';
import { computed } from 'vue';
import { AccountAppStatus, AccountFilterType } from './mixins';

export function useAccountList() {
    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            type: { type: 'number', default: AccountFilterType.All.value },
            criteria: { type: 'string', default: '' },
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const validator = {
        criteria: {
            requiredIf: requiredIf(() => query.type !== AccountFilterType.All.value),
            valueValid: computed(() => {
                switch (query.type) {
                    case AccountFilterType.All.value:
                        return true;
                    case AccountFilterType.Name.value:
                        return minLength(3);
                    case AccountFilterType.Email.value:
                        return email;
                    case AccountFilterType.FacebookID.value:
                    case AccountFilterType.AccountID.value:
                    case AccountFilterType.CreditBuy.value:
                        return numeric;
                    default:
                        return false;
                }
            })
        }
    };

    const v$ = useVuelidate(validator, query);

    const { list, total, fetchList } = useBaseListView({
        request: applicationService.account.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute,
            canFetch: () => {
                v$.value.$touch();
                return !v$.value.$invalid;
            },
            transformResponse: (list: any[]) =>
                list.map(item => {
                    let status = AccountAppStatus.None.value;
                    if (item.isBanned) status = AccountAppStatus.Banned.value;
                    if (item.isBanChat) status = AccountAppStatus.BanChat.value;
                    if (item.isMute) status = AccountAppStatus.Mute.value;
                    if (item.isDeleting) status = AccountAppStatus.Deleting.value;

                    return { ...item, status };
                })
        }
    });

    return { v$, query, list, total, fetchList };
}
